import { isEqual } from 'lodash-es';
import React, {
  PropsWithChildren,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import { shallow } from 'zustand/shallow';

import { Dropdown, Icon, Tooltip } from '@column/column-ui-kit';
import { DataTable, type DataTableColumnDef, Timestamp } from '@column/ui';

import { FilterDropdown, ResetFilter } from '~/app/pages/Transfers/Overview';
import { ButtonGroupDropdown } from '~/components/ButtonGroupDropdown';
import { useBankAccountOptions } from '~/components/fields/BankAccountDropdown';
import {
  type EmptyProps,
  StatusBadge,
  Empty as TableEmpty,
  Pagination as TablePagination,
  tableDefaults,
} from '~/components/Table';
import { useQueryParams } from '~/hooks';
import { ReportsFilterType, useReporting } from '~/hooks/useReporting';
import {
  DocumentExtensionType,
  DocumentRepository,
  DocumentTypeMap,
  Report,
  ReportingListParams,
} from '~/repositories';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { ReportingSettlementReport } from '~/typings/API';
import { downloadFile, formatDateTime, formatString, getFormattedDate } from '~/util';
import { MonthBoundariesType } from '~/util/dateFilterHelpers';
import { getFilenameForStatementReport } from '~/util/reporting';

import { DownloadButton } from './DownloadButton';
import { Loading } from './Loading';
import { PopoverFilterPeriod, PopoverFilterWrapper } from './Popover';

export interface ReportingTableProps {
  tableId: string;
  bankAccountId?: string;
  className?: string;
  empty?: EmptyProps;
  action?: ReactNode;
  onRowClick?: (entry: ReportingSettlementReport) => void;
  filter?: ReportsFilterType;
  visibleFiles?: DocumentExtensionType[];
  hiddenFiles?: DocumentExtensionType[];
  overwriteEntryObject?: (entry: ReportingSettlementReport | Report, object: object) => object;
}

export interface ReportingTableRefProps {
  fetch: (params?: ReportsFilterType) => void;
  reset: () => void;
  setQueryParams: (object?: ReportsFilterType) => void;
}

type ReportingTableRow = Record<string, any>;

const DateRange = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 0;
`;

const Arrow = styled(Icon.ArrowRight)`
  display: inline-block;
  vertical-align: middle;

  --icon-size: 16px;
  --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
`;

const FilterWrapper = styled.div`
  margin-right: auto;
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ActionWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;

  & > *:only-child {
    justify-self: start;
  }
`;

export const ReportingTable = forwardRef<ReportingTableRefProps, PropsWithChildren<ReportingTableProps>>(
  (
    {
      bankAccountId,
      filter,
      action,
      overwriteEntryObject,
      visibleFiles = ['csv', 'parquet'],
      hiddenFiles = [],
      ...props
    },
    ref
  ) => {
    const { updateSettings } = useSessionStore();
    const [isMounted, setIsMounted] = useState<boolean>(false);
    const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
    const { timeFormat, update: updateTableConfig } = useTableConfig(props.tableId);

    const defaultFilters = useMemo(() => {
      const filters: ReportsFilterType = { ...filter };

      return filters;
    }, [filter]);

    const { response, queryParams, isLoading, isInitialLoad, setQueryParams } = useReporting({
      onInitialLoad: () => setIsMounted(true),
      initialParams: defaultFilters,
      persistParams: bankAccountId
        ? {
            statementSubjectId: bankAccountId,
          }
        : undefined,
    });

    const { queryParams: urlQueryParams } = useQueryParams<ReportingListParams>();

    const [filterPeriodData, setFilterPeriodData] = useState<Partial<MonthBoundariesType>>({});
    const isFilterPeriodActive = useMemo(() => {
      if (filterPeriodData?.fromDate || filterPeriodData?.toDate) {
        return true;
      }
      return false;
    }, [filterPeriodData]);

    const [bankAccountIdFilterData, setBankAccountIdFilterData] = useState<string | undefined>(bankAccountId);

    const {
      options: bankAccountList,
      hiddenOptions: bankAccountHiddenOptions,
      search: searchBankAccounts,
    } = useBankAccountOptions({ value: bankAccountIdFilterData });

    const isFilterActive = useMemo(
      () => (!bankAccountId ? isFilterPeriodActive || bankAccountIdFilterData : isFilterPeriodActive),
      [isFilterPeriodActive, bankAccountIdFilterData, bankAccountId]
    );

    const handleFilterReset = useCallback(() => {
      setFilterPeriodData({});
      setBankAccountIdFilterData(undefined);

      setQueryParams(
        {
          limit: queryParams?.limit,
          page: 1,
          ...defaultFilters,
        },
        true
      );
    }, [queryParams, defaultFilters]);

    useLayoutEffect(() => {
      if (!urlQueryParams) {
        return;
      }

      const filterPeriodDataCompare: Partial<MonthBoundariesType> = {
        toDate: urlQueryParams?.toDate,
        fromDate: urlQueryParams?.fromDate,
      };

      if (!isEqual(filterPeriodData, filterPeriodDataCompare)) {
        setFilterPeriodData(filterPeriodDataCompare);
      }

      if (urlQueryParams?.category === 'statement' && !bankAccountId) {
        setBankAccountIdFilterData(urlQueryParams.statementSubjectId);
      }
    }, []);

    useEffect(() => {
      const newQueryParams: ReportsFilterType = {
        ...queryParams,
        ...filterPeriodData,
      };

      if (!bankAccountId) {
        newQueryParams.statementSubjectId = bankAccountIdFilterData;
      }

      const queryParamsCompare = { ...queryParams };

      if (!isInitialLoad && !isEqual(newQueryParams, queryParamsCompare)) {
        setQueryParams({
          ...newQueryParams,
          startingAfter: undefined,
          endingBefore: undefined,
          page: 1,
        });

        return;
      }

      setQueryParams({ ...newQueryParams, ...defaultFilters });
    }, [filterPeriodData, bankAccountIdFilterData, defaultFilters, bankAccountId]);

    const handleTimeFormatChange = useCallback(
      (value: TableTimeFormat) => {
        updateTableConfig({ timeFormat: value }).catch(() => {
          addDangerNotification({ content: 'Failed to save time format preference' });
        });
      },
      [addDangerNotification, updateTableConfig]
    );

    const columns: DataTableColumnDef<ReportingTableRow>[] = [
      {
        header: 'Information',
        accessor: 'information',
        width: 'minmax(200px, auto)',
        className: 'font-medium',
        cell: ({ value }) => value,
      },
      {
        header: 'Type',
        accessor: 'type',
        width: 'minmax(min-content, 100px)',
        cell: ({ value }) => <StatusBadge>{formatString(value)}</StatusBadge>,
      },
      {
        header: 'Status',
        accessor: 'status',
        width: 'min-content',
        cell: ({ value }) => (
          <StatusBadge tone={value === 'completed' ? 'success' : value === 'failed' ? 'danger' : 'default'}>
            {formatString(value)}
          </StatusBadge>
        ),
      },
      {
        header: (
          <Dropdown
            size="small"
            positionRight
            active={timeFormat}
            options={[
              { label: 'Relative Time', value: TableTimeFormat.Relative },
              { label: 'Date / Time', value: TableTimeFormat.Absolute },
            ]}
            onChange={handleTimeFormatChange}
            customLabel={
              <span className="flex items-center gap-1">
                Created <Icon.ChevronDown />
              </span>
            }
          />
        ),
        accessor: 'created',
        width: 'min-content',
        cell: ({ value }) =>
          value ? (
            <Timestamp
              date={value}
              align="right"
              className="text-xs text-muted-foreground"
              formatHover={(hoverDate) =>
                formatDateTime(hoverDate, {
                  timeZoneFormat: timeFormat === TableTimeFormat.Relative ? 'local' : 'utc',
                })
              }
            >
              {timeFormat === TableTimeFormat.Relative
                ? getFormattedDate(value, 'time-passed')
                : formatDateTime(value, { timeZoneFormat: 'local' })}
            </Timestamp>
          ) : (
            '-'
          ),
      },
      {
        header: '',
        accessor: 'action',
        width: 'max-content',
        className: 'justify-end',
        cell: ({ value }) => value,
      },
    ];

    const handlePrev = useCallback(() => {
      setQueryParams({
        startingAfter: undefined,
        endingBefore: Number(queryParams?.page) - 1 === 1 ? undefined : response?.reports?.at(0)?.id,
        page: Number(queryParams?.page ?? 1) - 1,
        ...defaultFilters,
      });
    }, [queryParams, response?.reports, defaultFilters]);

    const handleNext = useCallback(() => {
      setQueryParams({
        startingAfter: response?.reports && Array.isArray(response?.reports) ? response.reports?.at(-1)?.id : undefined,
        endingBefore: undefined,
        page: Number(queryParams?.page ?? 1) + 1,
        ...defaultFilters,
      });
    }, [queryParams, response?.reports, defaultFilters]);

    useImperativeHandle(
      ref,
      () => ({
        setQueryParams,
        reset: () => {
          setQueryParams(
            {
              limit: queryParams?.limit,
              startingAfter: undefined,
              endingBefore: undefined,
              page: 1,
              ...defaultFilters,
            },
            true
          );
        },
        fetch: (params?: ReportsFilterType) => {
          setQueryParams({
            ...queryParams,
            ...params,
            ...defaultFilters,
          });
        },
      }),
      [queryParams, defaultFilters]
    );

    useEffect(
      () =>
        useSessionStore.subscribe(
          (state) =>
            !state.settings.tableSettings || !state.settings.tableSettings[props.tableId]
              ? 10
              : state.settings.tableSettings[props.tableId].limit,
          (limit) => {
            if (limit !== queryParams?.limit) {
              setQueryParams({
                limit,
                ...defaultFilters,
              });
            }
          },
          {
            fireImmediately: true,
          }
        ),
      [queryParams]
    );

    useEffect(
      () =>
        useSessionStore.subscribe(
          (state) => [state.isSandbox, state.currentUser?.defaultPlatformId],
          () => {
            if (isMounted) {
              return;
            }

            handleFilterReset();
          },
          {
            equalityFn: shallow,
          }
        ),
      [isMounted]
    );

    const handleDownload = useCallback(
      (fileName: string, docId?: string) => {
        if (!docId) {
          return;
        }

        DocumentRepository.get(docId, { contentDisposition: `attachment; filename="${fileName}"` })
          .then((file) => downloadFile(file.url, fileName))
          .catch((error) =>
            addDangerNotification({
              content: error.message,
            })
          );
      },
      [addDangerNotification]
    );

    const entryObject = useCallback(
      (entries?: ReportingSettlementReport[]): ReportingTableRow[] => {
        if (!entries) {
          return [];
        }

        return entries.map((entry: Report) => {
          const object = {
            id: entry.id,
            information: (
              <DateRange>
                {entry.fromDate} <Arrow /> {entry.toDate}
              </DateRange>
            ),
            type: entry.type,
            status: entry.status,
            created: entry.createdAt,
            action: (
              <ButtonGroupDropdown
                type="dots"
                buttons={[...DocumentTypeMap]
                  .filter(
                    ([ext]) =>
                      visibleFiles.includes(ext as DocumentExtensionType) &&
                      !hiddenFiles.includes(ext as DocumentExtensionType)
                  )
                  .map(([ext, { label, fieldId }]) => (
                    <Tooltip
                      key={ext}
                      content={`No .${ext} file found`}
                      isOpen={entry[fieldId as keyof Report] ? false : undefined}
                    >
                      <DownloadButton
                        label={label}
                        fileName={getFilenameForStatementReport(entry, ext)}
                        id={String(entry[fieldId as keyof Report])}
                        isDisabled={!entry[fieldId as keyof Report]}
                      />
                    </Tooltip>
                  ))}
                options={[...DocumentTypeMap]
                  .filter(
                    ([ext]) =>
                      !visibleFiles.includes(ext as DocumentExtensionType) &&
                      !hiddenFiles.includes(ext as DocumentExtensionType)
                  )
                  .map(([ext, { label, fieldId }]) => ({
                    icon: !entry[fieldId as keyof Report] ? <Icon.CircleCross /> : <Icon.AnimationDownload />,
                    label,
                    isDisabled: !entry[fieldId as keyof Report],
                    tooltip: !entry[fieldId as keyof Report] ? { content: `No .${ext} file found` } : undefined,
                    onClick: () =>
                      handleDownload(getFilenameForStatementReport(entry, ext), String(entry[fieldId as keyof Report])),
                  }))}
              />
            ),
          };

          return (
            typeof overwriteEntryObject !== 'undefined' ? overwriteEntryObject(entry, object) : object
          ) as ReportingTableRow;
        });
      },
      [overwriteEntryObject, visibleFiles, hiddenFiles, handleDownload]
    );

    const empty = props.empty ?? { title: 'No entries found' };

    return (
      <Loading isLoading={isInitialLoad}>
        <div className={props.className}>
          {props.children}
          <div className="px-[var(--page-margin)] pb-3">
            <ActionWrapper>
              <FilterWrapper>
                {!bankAccountId && defaultFilters?.category === 'statement' && (
                  <FilterDropdown
                    options={bankAccountList}
                    hiddenOptions={bankAccountHiddenOptions}
                    size="small"
                    active={bankAccountIdFilterData}
                    $isFilterActive={bankAccountIdFilterData !== undefined}
                    label={
                      bankAccountHiddenOptions?.find((b) => b.value === bankAccountIdFilterData)?.label || 'Account'
                    }
                    search
                    searchLabel="Search for description"
                    onSearchChange={(description: string) => searchBankAccounts(description)}
                    onChange={setBankAccountIdFilterData}
                    maxWidth="480px"
                    emptyMessage="No accounts found"
                  />
                )}

                <PopoverFilterWrapper label="Date" onSubmit={setFilterPeriodData} isActive={isFilterPeriodActive}>
                  <PopoverFilterPeriod data={filterPeriodData ?? {}} reportCategory={filter?.category} />
                </PopoverFilterWrapper>

                {isFilterActive && (
                  <ResetFilter
                    size="small"
                    variant="subtle-primary"
                    icon={<Icon.Reset />}
                    iconRight
                    onClick={handleFilterReset}
                  >
                    Reset filters
                  </ResetFilter>
                )}
              </FilterWrapper>
              {action}
            </ActionWrapper>
          </div>
          <div className="px-[var(--page-margin)]">
            <DataTable<ReportingTableRow>
              {...tableDefaults}
              columns={columns}
              data={entryObject(response?.reports)}
              isLoading={isLoading}
              rowKey={(row, index) => row.id ?? index}
              emptyContent={
                <TableEmpty
                  {...empty}
                  description={
                    isFilterActive ? 'No entries were found matching the specified filters' : empty.description
                  }
                  action={isFilterActive ? undefined : empty.action}
                />
              }
              onRowClick={props.onRowClick ? (row) => props.onRowClick?.(row as ReportingSettlementReport) : undefined}
            />
          </div>
          <TablePagination
            className="px-[var(--page-margin)] pb-6"
            pageSize={queryParams?.limit ?? 10}
            onPageSizeChange={(value: number) => {
              updateSettings({
                tableSettings: {
                  [props.tableId]: {
                    limit: value,
                  },
                },
              }).then(() => {
                setQueryParams(
                  {
                    startingAfter: undefined,
                    endingBefore: undefined,
                    limit: value,
                    page: 1,
                    ...defaultFilters,
                  },
                  true
                );
              });
            }}
            current={Number(queryParams?.page ?? 1)}
            hasPrevious={Number(queryParams?.page ?? 1) !== 1}
            hasNext={Boolean(response?.hasMore)}
            onPrevious={handlePrev}
            onNext={handleNext}
          />
        </div>
      </Loading>
    );
  }
);
